import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { toStatus, type ApiItem } from "../../utils/applicationCards";
import { DEBRIEF_DAYS, detectOaKind, isCodingOa, leftOaOn, oaEvents, type Debrief, type PrepEventDoc } from "../../utils/prepEvents";
import { takeDay } from "../../utils/prepPlanner";
import { daysBetween } from "../../utils/prepSkills";
import { topicName } from "../../data/prep/topics";
import { localToday } from "../../utils/usePrepPlan";
import { OA_COLOR } from "./PrepOaCard";
import PrepDebrief from "./PrepDebrief";
import { shareDebrief } from "../../utils/companyDebriefs";

// On a card's sheet in Applications, for an OA: before it, how long until it
// with a link to its prep (and, if they chose to prep themselves on the Prep page, the way back). After it
// (its date passed, or the card moved on), the debrief, or what was said in it.

const WENT_LABEL: Record<Debrief["went"], string> = { well: "Went well", ok: "Went OK", rough: "Was rough" };

export default function PrepOaRow({ card }: { card: ApiItem }) {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [saved, setSaved] = useState<PrepEventDoc | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, "users", user.uid, "prepEvents", card.id),
      (snap) => setSaved((snap.data() as PrepEventDoc | undefined) ?? {}),
      (e) => console.error("Could not load OA prep:", e)
    );
  }, [user, card.id]);

  const atOa = toStatus(card.predicted_label) === "oa";
  const leftOn = atOa ? null : leftOaOn(card);
  if (!user || !saved || (!atOa && !leftOn)) return null;
  // Not a coding test (a form, a business role's assessment): nothing to prep or debrief
  if (!isCodingOa(card) && !saved.debrief) return null;
  const save = (patch: PrepEventDoc) =>
    setDoc(doc(db, "users", user.uid, "prepEvents", card.id), patch, { merge: true }).catch((e) =>
      console.error("Could not save OA prep:", e)
    );
  const link = "font-medium text-melt-text1 underline decoration-melt-text3/40 underline-offset-2 hover:decoration-melt-text1";
  const box = "mt-3 rounded-xl bg-[#F6E3D3]/60 px-3 py-2 text-[13px]";
  const today = localToday();

  // When it's over: the card moved on, or the day to take it has passed
  const [event] = atOa ? oaEvents([card], { [card.id]: { ...saved, selfPrep: false } }) : [];
  const left = event ? daysBetween(today, takeDay(event, today)) : -1;
  const over = !atOa || left < 0;

  if (over) {
    const { debrief } = saved;
    // Long gone from OA and never debriefed: too late to ask
    if (!debrief && !saved.debriefed && leftOn && daysBetween(leftOn, today) > DEBRIEF_DAYS) return null;
    if (asking || (!debrief && !saved.debriefed)) {
      return (
        <div className={box}>
          <p className="mb-2 font-medium text-[#7A3E1D]">How did the OA go?</p>
          <PrepDebrief
            company={card.company}
            kind={saved.kind ?? detectOaKind(card)}
            initial={debrief}
            today={today}
            onSave={(answers) => {
              save({
                debrief: { ...answers, ...(card.company ? { company: card.company } : {}), ...(card.role ? { role: card.role } : {}) },
                debriefed: true,
              });
              shareDebrief(user.uid, card, answers, saved.kind ?? detectOaKind(card)).catch((e) =>
                console.warn("Could not share the debrief:", e)
              );
              setAsking(false);
            }}
          />
        </div>
      );
    }
    const stuck = debrief?.topics.map(topicName).join(", ");
    return (
      <p className={`${box} flex flex-wrap items-center gap-x-2 text-melt-text2`}>
        <span className="font-medium text-[#7A3E1D]">OA debrief:</span>
        {debrief ? (
          <>
            {WENT_LABEL[debrief.went]}
            {debrief.levels !== undefined && ` · ${debrief.levels} of 4 levels`}
            {stuck && ` · stuck on ${stuck}`}
            {debrief.questions?.length ? ` · ${debrief.questions.length} ${debrief.questions.length === 1 ? "question" : "questions"} saved` : ""}
          </>
        ) : (
          "done"
        )}
        <button type="button" className={`${link} ml-auto`} onClick={() => setAsking(true)}>
          Edit
        </button>
      </p>
    );
  }

  if (saved.selfPrep) {
    return (
      <p className="mt-3 text-[13px] text-melt-text2">
        You’re prepping for this one yourself.{" "}
        <button type="button" className={link} onClick={() => save({ selfPrep: false })}>
          Plan prep for it
        </button>
      </p>
    );
  }

  if (!event) return null;
  const when = left > 1 ? `${left} days left` : left === 1 ? "OA tomorrow" : "OA today";

  return (
    <div className={`${box} flex flex-wrap items-center gap-x-3 gap-y-1`}>
      <span className="flex items-center gap-2 font-medium text-[#7A3E1D]">
        <i className="h-2 w-2 rounded-[2px]" style={{ background: OA_COLOR.dot }} aria-hidden="true" />
        Prep: {when}
      </span>
      <button type="button" className={link} onClick={() => navigate("/dashboard/prep")}>
        Open prep
      </button>
    </div>
  );
}
